// MARK://dhk8/pos/pages/NotificationsPage
// Bandeja de atención del staff (/avisos). Dos dominios que piden atención del
// staff, en pestañas:
//   · "Avisos"      — GuestNotification que el comensal manda desde la mesa
//                     (llamar mesero / pedir cuenta / algo más / problema).
//   · "Por aceptar" — órdenes del comensal en PENDING_ACCEPTANCE (aceptar/rechazar).
//
// Real-time (Fase 2): con <RealtimeProvider> montado (main.tsx) esta página se
// suscribe a `t:{tid}:notifications` y `t:{tid}:orders`; cada evento DISPARA un
// refetch del dato AUTORITATIVO por REST (robusto, sin depender del payload).
// Fallback: polling 15s por si el WS está caído. El indicador "En vivo/Actualizando"
// refleja el estado del socket.
//
// Tokens: --pos-fg/--pos-canvas/--pos-separator/--pos-tint/--pos-danger (válidos
// en Light Y Dark) — NO los viejos --pos-ink/surface/border (que reventaban en Light).

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Bell, RefreshCw, Check, Eye, Inbox, Clock, X, CreditCard, Banknote, Globe, AlertTriangle, Wifi, WifiOff } from 'lucide-react'
import { useRealtimeContext, useRealtimeSubscription } from '@dockethub/realtime'
import {
  listGuestNotifications,
  updateGuestNotificationStatus,
  type GuestNotificationDto,
  type GuestNotificationStatus,
  type GuestNotificationTopic,
} from '../lib/clients/floor.js'
import {
  listPendingAcceptanceOrders,
  acceptOrder,
  rejectOrder,
  type BackendOrderDto,
} from '../lib/clients/orders.js'
import { getTenantId } from '../lib/api.js'
import { useTenantId } from '../hooks/useTenantId.js'
import { useAuthStore } from '../stores/auth-store.js'
import { formatCents } from '../lib/money.js'

const POLL_MS = 15_000
const GUEST_SENTINEL = '00000000-0000-0000-0000-000000000001'

const TOPIC_META: Record<GuestNotificationTopic, { icon: string; label: string }> = {
  CALL_WAITER: { icon: '🙋', label: 'Llamar al mesero' },
  REQUEST_CHECK: { icon: '🧾', label: 'Pedir la cuenta' },
  REQUEST_ITEM: { icon: '🍴', label: 'Pedir algo más' },
  REPORT_ISSUE: { icon: '⚠️', label: 'Reportar un problema' },
  OTHER: { icon: '💬', label: 'Otro' },
}

type Tab = 'avisos' | 'por-aceptar'
type NotifFilter = 'active' | 'resolved'

function fmtElapsed(iso: string): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000))
  if (mins < 1) return 'ahora'
  if (mins < 60) return `${mins} min`
  return `${Math.floor(mins / 60)}h ${mins % 60}m`
}
function fmtTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat('es-MX', { hour: '2-digit', minute: '2-digit' }).format(d)
}
function shortFolio(orderId: string): string {
  return orderId.slice(0, 8).toUpperCase()
}
function methodMeta(method: string | null | undefined): { label: string; Icon: typeof CreditCard; known: boolean } {
  switch (method) {
    case 'CASH': return { label: 'Efectivo', Icon: Banknote, known: true }
    case 'TERMINAL': return { label: 'Terminal', Icon: CreditCard, known: true }
    case 'ONLINE': return { label: 'En línea', Icon: Globe, known: true }
    default: return { label: 'Sin método', Icon: AlertTriangle, known: false }
  }
}

export default function NotificationsPage() {
  const tenantId = useTenantId()
  const user = useAuthStore((s) => s.user)
  const { status: rtStatus, client } = useRealtimeContext()
  const live = client !== null && rtStatus === 'open'

  const [tab, setTab] = useState<Tab>('avisos')

  // ── Avisos (GuestNotification) ─────────────────────────────────────────────
  const [notifs, setNotifs] = useState<GuestNotificationDto[]>([])
  const [notifFilter, setNotifFilter] = useState<NotifFilter>('active')
  const [notifLoading, setNotifLoading] = useState(true)
  const [notifBusy, setNotifBusy] = useState<string | null>(null)

  // ── Por aceptar (PENDING_ACCEPTANCE) ───────────────────────────────────────
  const [orders, setOrders] = useState<BackendOrderDto[]>([])
  const [ordersLoading, setOrdersLoading] = useState(true)
  const [ordBusy, setOrdBusy] = useState<string | null>(null)
  const [cardError, setCardError] = useState<Record<string, string>>({})
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const [error, setError] = useState<string | null>(null)

  const reloadNotifs = useCallback(async (f: NotifFilter) => {
    try {
      setError(null)
      const r = await listGuestNotifications(f === 'resolved' ? { status: 'RESOLVED', limit: 50 } : { limit: 80 })
      setNotifs(r.items)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar notificaciones')
    } finally {
      setNotifLoading(false)
    }
  }, [])

  const reloadOrders = useCallback(async () => {
    const tid = getTenantId()
    if (tid === null || tid.length === 0) { setOrdersLoading(false); return }
    try {
      const items = await listPendingAcceptanceOrders(tid)
      setOrders(items.slice().sort((a, b) => a.openedAt.localeCompare(b.openedAt)))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar las órdenes por aceptar')
    } finally {
      setOrdersLoading(false)
    }
  }, [])

  // Carga inicial + polling de respaldo (ambos dominios; el WS de abajo acelera).
  useEffect(() => {
    setNotifLoading(true)
    void reloadNotifs(notifFilter)
    const iv = setInterval(() => void reloadNotifs(notifFilter), POLL_MS)
    return () => clearInterval(iv)
  }, [notifFilter, tenantId, reloadNotifs])

  useEffect(() => {
    setOrdersLoading(true)
    void reloadOrders()
    const iv = setInterval(() => void reloadOrders(), POLL_MS)
    return () => clearInterval(iv)
  }, [tenantId, reloadOrders])

  // WS: cada evento del canal dispara el refetch autoritativo (no-op sin provider).
  const onNotifEvent = useCallback(() => { void reloadNotifs(notifFilter) }, [reloadNotifs, notifFilter])
  const onOrderEvent = useCallback(() => { void reloadOrders() }, [reloadOrders])
  useRealtimeSubscription(tenantId !== null ? `t:${tenantId}:notifications` : '', onNotifEvent, [tenantId, notifFilter])
  useRealtimeSubscription(tenantId !== null ? `t:${tenantId}:orders` : '', onOrderEvent, [tenantId])

  async function setNotifStatus(id: string, status: GuestNotificationStatus) {
    setNotifBusy(id)
    try {
      await updateGuestNotificationStatus(id, status)
      await reloadNotifs(notifFilter)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al actualizar')
    } finally {
      setNotifBusy(null)
    }
  }

  const actor = user?.id ?? GUEST_SENTINEL
  async function accept(o: BackendOrderDto) {
    setOrdBusy(o.id)
    setCardError((m) => { const n = { ...m }; delete n[o.id]; return n })
    try {
      await acceptOrder(o.id, actor)
      await reloadOrders()
    } catch (e) {
      setCardError((m) => ({ ...m, [o.id]: e instanceof Error ? e.message : 'No se pudo aceptar la orden' }))
    } finally {
      setOrdBusy(null)
    }
  }
  async function confirmReject(o: BackendOrderDto) {
    const reason = rejectReason.trim()
    if (reason.length === 0) { setCardError((m) => ({ ...m, [o.id]: 'Escribe el motivo del rechazo.' })); return }
    setOrdBusy(o.id)
    try {
      await rejectOrder(o.id, actor, reason)
      setRejectingId(null); setRejectReason('')
      await reloadOrders()
    } catch (e) {
      setCardError((m) => ({ ...m, [o.id]: e instanceof Error ? e.message : 'No se pudo rechazar la orden' }))
    } finally {
      setOrdBusy(null)
    }
  }

  const notifPending = useMemo(() => notifs.filter((n) => n.status === 'PENDING').length, [notifs])
  const pendingOrders = orders.length

  return (
    <div className="dhk8-av-root">
      <header className="dhk8-av-head">
        <div>
          <div className="dhk8-av-eyebrow">Punto de venta · Servicio</div>
          <h1 className="dhk8-av-title">Avisos y órdenes por aceptar</h1>
          <div className="dhk8-av-sub">Todo lo que el comensal manda desde la mesa, en un solo lugar</div>
        </div>
        <span className={`dhk8-av-live${live ? ' is-live' : ''}`} title={live ? 'Actualización en vivo (WebSocket)' : 'Sin conexión en vivo — actualizando por sondeo'}>
          {live ? <Wifi size={13} aria-hidden /> : <WifiOff size={13} aria-hidden />}
          {live ? 'En vivo' : 'Sondeo'}
        </span>
      </header>

      <div className="dhk8-av-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'avisos'}
          className={`dhk8-av-tab${tab === 'avisos' ? ' is-active' : ''}`} onClick={() => setTab('avisos')}>
          <Bell size={15} aria-hidden /> Avisos
          {notifPending > 0 && <span className="dhk8-av-tabcount">{notifPending}</span>}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'por-aceptar'}
          className={`dhk8-av-tab${tab === 'por-aceptar' ? ' is-active' : ''}`} onClick={() => setTab('por-aceptar')}>
          <Inbox size={15} aria-hidden /> Por aceptar
          {pendingOrders > 0 && <span className="dhk8-av-tabcount is-danger">{pendingOrders}</span>}
        </button>
        <span className="dhk8-av-tabs__spacer" />
        <button type="button" className="dhk8-av-refresh"
          onClick={() => tab === 'avisos' ? void reloadNotifs(notifFilter) : void reloadOrders()} title="Recargar">
          <RefreshCw size={14} /> Recargar
        </button>
      </div>

      {error && <div className="dhk8-av-err" role="alert">{error}</div>}

      {/* ── TAB AVISOS ────────────────────────────────────────────────────── */}
      {tab === 'avisos' && (
        <>
          <div className="dhk8-av-subfilters">
            <button type="button" className={`dhk8-av-chip${notifFilter === 'active' ? ' is-active' : ''}`} onClick={() => setNotifFilter('active')}>Activas</button>
            <button type="button" className={`dhk8-av-chip${notifFilter === 'resolved' ? ' is-active' : ''}`} onClick={() => setNotifFilter('resolved')}>Atendidas</button>
          </div>
          {notifLoading && <div className="dhk8-av-empty">Cargando notificaciones…</div>}
          {!notifLoading && notifs.length === 0 && (
            <div className="dhk8-av-empty">{notifFilter === 'active' ? 'Sin notificaciones pendientes.' : 'Sin notificaciones atendidas.'}</div>
          )}
          <ul className="dhk8-av-list">
            {notifs.map((n) => {
              const meta = TOPIC_META[n.topic] ?? TOPIC_META.OTHER
              return (
                <li key={n.id} className={`dhk8-av-ncard status-${n.status.toLowerCase()}`}>
                  <span className="dhk8-av-ncard__icon" aria-hidden>{meta.icon}</span>
                  <div className="dhk8-av-ncard__body">
                    <div className="dhk8-av-ncard__row1">
                      <strong className="dhk8-av-ncard__table">Mesa {n.tableCode ?? '—'}</strong>
                      {n.status === 'PENDING' && <span className="dhk8-av-badge tone-pending">Pendiente</span>}
                      {n.status === 'ACKNOWLEDGED' && <span className="dhk8-av-badge tone-ack">Vista</span>}
                      {n.status === 'RESOLVED' && <span className="dhk8-av-badge tone-done">Atendida</span>}
                    </div>
                    <div className="dhk8-av-ncard__topic">{meta.label}</div>
                    {n.note && <div className="dhk8-av-ncard__note">“{n.note}”</div>}
                    <div className="dhk8-av-ncard__meta">{fmtTime(n.createdAt)} · hace {fmtElapsed(n.createdAt)}</div>
                  </div>
                  {n.status !== 'RESOLVED' && (
                    <div className="dhk8-av-ncard__actions">
                      {n.status === 'PENDING' && (
                        <button type="button" className="dhk8-av-btn" disabled={notifBusy === n.id} onClick={() => void setNotifStatus(n.id, 'ACKNOWLEDGED')} title="Marcar como vista">
                          <Eye size={13} /> Vista
                        </button>
                      )}
                      <button type="button" className="dhk8-av-btn primary" disabled={notifBusy === n.id} onClick={() => void setNotifStatus(n.id, 'RESOLVED')} title="Marcar como atendida">
                        <Check size={13} /> Atendida
                      </button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}

      {/* ── TAB POR ACEPTAR ───────────────────────────────────────────────── */}
      {tab === 'por-aceptar' && (
        <>
          {ordersLoading && <div className="dhk8-av-empty">Cargando la bandeja…</div>}
          {!ordersLoading && orders.length === 0 && (
            <div className="dhk8-av-empty dhk8-av-empty--zen">
              <span className="dhk8-av-empty__icon" aria-hidden>🍃</span>
              <span>Nada por aceptar. Las órdenes del comensal aparecerán aquí.</span>
            </div>
          )}
          <ol className="dhk8-av-list" aria-label="Órdenes por aceptar">
            {orders.map((o) => {
              const m = methodMeta(o.chosenPaymentMethod)
              const isRejecting = rejectingId === o.id
              const busy = ordBusy === o.id
              return (
                <li key={o.id} className="dhk8-av-ocard">
                  <div className="dhk8-av-ocard__head">
                    <div className="dhk8-av-ocard__head-left">
                      <strong className="dhk8-av-ocard__folio">#{shortFolio(o.id)}</strong>
                      <span className={`dhk8-av-method${m.known ? '' : ' dhk8-av-method--none'}`}><m.Icon size={12} aria-hidden /> {m.label}</span>
                    </div>
                    <span className="dhk8-av-wait"><Clock size={12} aria-hidden /> {fmtElapsed(o.openedAt)}</span>
                  </div>
                  <ul className="dhk8-av-items">
                    {o.items.filter((it) => it.status !== 'CANCELLED').map((it) => (
                      <li key={it.id} className="dhk8-av-item">
                        <span className="dhk8-av-item__qty">{it.quantity}×</span>
                        <span className="dhk8-av-item__name">{it.name}{it.modifiers.length > 0 && <span className="dhk8-av-item__mods"> · {it.modifiers.map((mm) => mm.name).join(', ')}</span>}{it.notes && <span className="dhk8-av-item__mods"> · {it.notes}</span>}</span>
                        <span className="dhk8-av-item__price">{formatCents(it.totalCents)}</span>
                      </li>
                    ))}
                  </ul>
                  {cardError[o.id] && <div className="dhk8-av-carderr" role="alert">{cardError[o.id]}</div>}
                  <div className="dhk8-av-ocard__foot">
                    <div className="dhk8-av-total"><span className="dhk8-av-total__label">Total</span><strong className="dhk8-av-total__amount">{formatCents(o.totalCents)}</strong></div>
                    {!isRejecting ? (
                      <div className="dhk8-av-ocard__actions">
                        <button type="button" className="dhk8-av-secondary dhk8-av-secondary--danger" disabled={busy} onClick={() => { setRejectingId(o.id); setRejectReason(''); setCardError((mm) => { const n = { ...mm }; delete n[o.id]; return n }) }} title="Rechazar (requiere motivo)"><X size={14} /> Rechazar</button>
                        <button type="button" className="dhk8-av-accept" disabled={busy} onClick={() => void accept(o)} title="Aceptar y enviar a la ventana de gracia">{busy ? 'Aceptando…' : <><Check size={15} /> Aceptar</>}</button>
                      </div>
                    ) : (
                      <div className="dhk8-av-reject">
                        <input className="dhk8-av-reject__input" autoFocus placeholder="Motivo del rechazo…" value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') void confirmReject(o); if (e.key === 'Escape') { setRejectingId(null); setRejectReason('') } }} />
                        <button type="button" className="dhk8-av-secondary" disabled={busy} onClick={() => { setRejectingId(null); setRejectReason('') }}>Cancelar</button>
                        <button type="button" className="dhk8-av-secondary dhk8-av-secondary--danger" disabled={busy} onClick={() => void confirmReject(o)}>{busy ? 'Rechazando…' : 'Confirmar'}</button>
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        </>
      )}

      <style>{styles}</style>
    </div>
  )
}

// Tokens theme-safe (--pos-fg/canvas/separator/tint/danger): correctos en Light Y Dark.
const styles = `
  .dhk8-av-root { display: flex; flex-direction: column; gap: var(--pos-space-3, 12px); padding: var(--pos-space-4, 16px); max-width: 820px; }
  .dhk8-av-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .dhk8-av-eyebrow { font-size: var(--pos-text-xxs, 11px); letter-spacing: .08em; text-transform: uppercase; color: var(--pos-fg-tertiary, #7E8C82); font-weight: 600; }
  .dhk8-av-title { margin: 3px 0 0; font-size: var(--pos-text-xl, 20px); font-weight: 700; color: var(--pos-fg, #1a1a1a); font-family: var(--font-display, system-ui); }
  .dhk8-av-sub { font-size: var(--pos-text-xs, 12.5px); color: var(--pos-fg-secondary, #666); margin-top: 2px; }
  .dhk8-av-live { display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0; font-size: var(--pos-text-xxs, 11px); font-weight: 700; text-transform: uppercase; letter-spacing: .04em; padding: 4px 9px; color: var(--pos-fg-tertiary, #7E8C82); border: 1px solid var(--pos-separator-strong, rgba(128,128,128,.28)); }
  .dhk8-av-live.is-live { color: var(--pos-tint-on, #1F8B3A); border-color: color-mix(in oklch, var(--pos-tint, #1F8B3A) 45%, transparent); background: var(--pos-tint-soft, rgba(31,139,58,.12)); }

  .dhk8-av-tabs { display: flex; align-items: center; gap: 6px; border-bottom: 1px solid var(--pos-separator, rgba(128,128,128,.18)); }
  .dhk8-av-tabs__spacer { flex: 1; }
  .dhk8-av-tab { display: inline-flex; align-items: center; gap: 6px; background: transparent; color: var(--pos-fg-secondary, #666); border: none; border-bottom: 2px solid transparent; padding: 9px 6px; margin-bottom: -1px; font: inherit; font-size: var(--pos-text-sm, 13.5px); font-weight: 600; cursor: pointer; }
  .dhk8-av-tab.is-active { color: var(--pos-fg, #1a1a1a); border-bottom-color: var(--pos-tint, #1F8B3A); }
  .dhk8-av-tabcount { min-width: 18px; height: 18px; padding: 0 5px; border-radius: var(--radius-full, 999px); background: var(--pos-fg-tertiary, #7E8C82); color: var(--pos-on-tint, #fff); font-size: 10.5px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }
  .dhk8-av-tabcount.is-danger { background: var(--pos-danger, #E5484D); }
  .dhk8-av-refresh { background: transparent; color: var(--pos-fg-secondary, #666); border: 1px solid var(--pos-separator-strong, rgba(128,128,128,.28)); padding: 6px 12px; font: inherit; font-size: var(--pos-text-xs, 12.5px); cursor: pointer; display: inline-flex; align-items: center; gap: 5px; }
  .dhk8-av-refresh:hover { color: var(--pos-fg, #1a1a1a); border-color: var(--pos-tint, #1F8B3A); }

  .dhk8-av-subfilters { display: inline-flex; gap: 4px; }
  .dhk8-av-chip { background: transparent; color: var(--pos-fg-secondary, #666); border: 1px solid var(--pos-separator-strong, rgba(128,128,128,.28)); padding: 6px 14px; font: inherit; font-size: 13px; cursor: pointer; }
  .dhk8-av-chip.is-active { color: var(--pos-tint-on, #1F8B3A); border-color: var(--pos-tint, #1F8B3A); background: var(--pos-tint-soft, rgba(31,139,58,.1)); }

  .dhk8-av-err { color: var(--pos-danger, #E5484D); font-size: var(--pos-text-sm, 13px); padding: 9px 12px; border: 1px solid color-mix(in oklch, var(--pos-danger, #E5484D) 35%, transparent); background: var(--pos-danger-bg, rgba(229,72,77,.09)); }
  .dhk8-av-empty { color: var(--pos-fg-tertiary, #7E8C82); font-size: var(--pos-text-sm, 13.5px); padding: 32px 12px; text-align: center; font-style: italic; }
  .dhk8-av-empty--zen { display: flex; flex-direction: column; align-items: center; gap: 8px; border: 1px dashed var(--pos-separator-strong, rgba(128,128,128,.28)); padding: 44px 16px; font-style: normal; }
  .dhk8-av-empty__icon { font-size: 28px; }

  .dhk8-av-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }

  /* Avisos card */
  .dhk8-av-ncard { display: flex; align-items: flex-start; gap: 12px; background: var(--pos-surface, var(--pos-canvas, #fff)); border: 1px solid var(--pos-separator-strong, rgba(128,128,128,.28)); padding: 12px 14px; }
  .dhk8-av-ncard.status-pending { border-left: 3px solid var(--pos-danger, #E5484D); }
  .dhk8-av-ncard.status-acknowledged { border-left: 3px solid var(--pos-warning, #E0A23C); }
  .dhk8-av-ncard.status-resolved { opacity: .6; border-left: 3px solid var(--pos-separator-strong, rgba(128,128,128,.28)); }
  .dhk8-av-ncard__icon { font-size: 24px; line-height: 1; margin-top: 2px; }
  .dhk8-av-ncard__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
  .dhk8-av-ncard__row1 { display: flex; align-items: center; gap: 8px; }
  .dhk8-av-ncard__table { font-size: 14.5px; color: var(--pos-fg, #1a1a1a); }
  .dhk8-av-ncard__topic { font-size: 13px; color: var(--pos-fg-secondary, #666); }
  .dhk8-av-ncard__note { font-size: 12.5px; color: var(--pos-fg-tertiary, #7E8C82); font-style: italic; }
  .dhk8-av-ncard__meta { font-size: 11.5px; color: var(--pos-fg-tertiary, #7E8C82); margin-top: 1px; }
  .dhk8-av-badge { font-size: 9.5px; padding: 2px 7px; text-transform: uppercase; letter-spacing: .04em; font-weight: 700; }
  .dhk8-av-badge.tone-pending { background: var(--pos-danger-bg, rgba(229,72,77,.16)); color: var(--pos-danger, #E5484D); }
  .dhk8-av-badge.tone-ack { background: var(--pos-warning-bg, rgba(224,162,60,.18)); color: var(--pos-warning, #E0A23C); }
  .dhk8-av-badge.tone-done { background: color-mix(in oklch, var(--pos-fg-tertiary, #7E8C82) 18%, transparent); color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-av-ncard__actions { display: flex; flex-direction: column; gap: 6px; flex-shrink: 0; }
  .dhk8-av-btn { background: transparent; color: var(--pos-fg-secondary, #666); border: 1px solid var(--pos-separator-strong, rgba(128,128,128,.28)); padding: 6px 10px; font: inherit; font-size: 12px; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
  .dhk8-av-btn:hover:not(:disabled) { color: var(--pos-fg, #1a1a1a); }
  .dhk8-av-btn.primary { color: var(--pos-tint-on, #1F8B3A); border-color: var(--pos-tint, #1F8B3A); }
  .dhk8-av-btn:disabled { opacity: .45; cursor: not-allowed; }

  /* Orden por aceptar card */
  .dhk8-av-ocard { background: var(--pos-surface, var(--pos-canvas, #fff)); border: 1px solid var(--pos-separator-strong, rgba(128,128,128,.28)); border-left: 4px solid var(--pos-tint, #1F8B3A); padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; }
  .dhk8-av-ocard__head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
  .dhk8-av-ocard__head-left { display: flex; align-items: center; gap: 10px; min-width: 0; flex-wrap: wrap; }
  .dhk8-av-ocard__folio { font-family: var(--font-mono, ui-monospace); font-size: 15px; color: var(--pos-fg, #1a1a1a); font-weight: 600; }
  .dhk8-av-method { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 600; padding: 3px 8px; color: var(--pos-fg-secondary, #666); border: 1px solid var(--pos-separator-strong, rgba(128,128,128,.28)); }
  .dhk8-av-method--none { color: var(--pos-warning, #E0A23C); border-color: color-mix(in oklch, var(--pos-warning, #E0A23C) 40%, transparent); }
  .dhk8-av-wait { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 600; color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-av-items { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .dhk8-av-item { display: flex; align-items: baseline; gap: 8px; font-size: 13px; color: var(--pos-fg-secondary, #666); }
  .dhk8-av-item__qty { color: var(--pos-tint-on, #1F8B3A); font-weight: 700; flex-shrink: 0; font-variant-numeric: tabular-nums; }
  .dhk8-av-item__name { flex: 1; min-width: 0; }
  .dhk8-av-item__mods { color: var(--pos-fg-tertiary, #7E8C82); font-size: 12px; }
  .dhk8-av-item__price { color: var(--pos-fg, #1a1a1a); flex-shrink: 0; font-variant-numeric: tabular-nums; }
  .dhk8-av-carderr { color: var(--pos-danger, #E5484D); font-size: 12px; padding: 7px 10px; border: 1px solid color-mix(in oklch, var(--pos-danger, #E5484D) 35%, transparent); background: var(--pos-danger-bg, rgba(229,72,77,.10)); }
  .dhk8-av-ocard__foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; border-top: 1px dashed var(--pos-separator, rgba(128,128,128,.18)); padding-top: 10px; }
  .dhk8-av-total { display: flex; flex-direction: column; gap: 1px; }
  .dhk8-av-total__label { font-size: 11px; color: var(--pos-fg-tertiary, #7E8C82); text-transform: uppercase; letter-spacing: .04em; }
  .dhk8-av-total__amount { font-size: 20px; color: var(--pos-fg, #1a1a1a); font-variant-numeric: tabular-nums; font-weight: 600; }
  .dhk8-av-ocard__actions, .dhk8-av-reject { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .dhk8-av-reject__input { flex: 1; min-width: 160px; background: var(--pos-canvas, #fff); color: var(--pos-fg, #1a1a1a); border: 1px solid var(--pos-separator-strong, rgba(128,128,128,.28)); padding: 9px 12px; font: inherit; font-size: 13px; min-height: 40px; }
  .dhk8-av-reject__input:focus { outline: none; border-color: var(--pos-tint, #1F8B3A); }
  .dhk8-av-secondary { background: transparent; color: var(--pos-fg-secondary, #666); border: 1px solid var(--pos-separator-strong, rgba(128,128,128,.28)); padding: 9px 12px; font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; min-height: 40px; }
  .dhk8-av-secondary:hover:not(:disabled) { color: var(--pos-fg, #1a1a1a); }
  .dhk8-av-secondary--danger { color: var(--pos-danger, #E5484D); border-color: color-mix(in oklch, var(--pos-danger, #E5484D) 35%, transparent); }
  .dhk8-av-secondary:disabled { opacity: .55; cursor: not-allowed; }
  .dhk8-av-accept { background: var(--pos-tint, #1F8B3A); color: var(--pos-on-tint, #fff); border: 1px solid var(--pos-tint, #1F8B3A); padding: 9px 16px; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; min-height: 40px; }
  .dhk8-av-accept:hover:not(:disabled) { filter: brightness(1.08); }
  .dhk8-av-accept:disabled { opacity: .55; cursor: not-allowed; }

  @media (max-width: 560px) {
    .dhk8-av-ocard__foot { align-items: stretch; }
    .dhk8-av-ocard__actions, .dhk8-av-reject { width: 100%; }
    .dhk8-av-accept, .dhk8-av-ocard__actions .dhk8-av-secondary { flex: 1; justify-content: center; }
  }
`
